import React, { useState } from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import { GardenProvider } from './context/GardenContext'
import Home from './pages/Home'
import GardenDesigner from './pages/GardenDesigner'
import PlantDatabase from './pages/PlantDatabase'
import Calendar from './pages/Calendar'
import WeatherAlertsPage from './pages/WeatherAlertsPage'
import Dashboard from './pages/Dashboard'
import AuthModal from './components/AuthModal'
import PremiumBanner from './components/PremiumBanner'
import PricingPage from './components/PricingPage'
import { OnboardingWizard } from './components/OnboardingWizard'
import { TEST_MODE, hasPremiumAccess } from './config/testMode'
import './index.css'

interface User {
  id: string
  email: string
  zipCode?: string
  frostDate?: Date
  isPremium: boolean
  isNewUser?: boolean
}

interface OnboardingData {
  interests: string[]
  growingMethod: string
  space: string
  zone: string
  experience: string
}

function App() {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showAuth, setShowAuth] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [, setOnboardingData] = useState<OnboardingData | null>(null)
  const location = useLocation()

  const handleAuth = (userData: User) => {
    // In test mode, auto-upgrade all users to premium
    if (TEST_MODE.AUTO_PREMIUM) {
      userData.isPremium = true
    }
    // Mark as new user to trigger onboarding
    userData.isNewUser = true
    setUser(userData)
    localStorage.setItem('gardenPlannerUser', JSON.stringify(userData))
    setShowAuth(false)
    // Show onboarding for new users
    setShowOnboarding(true)
  }

  const handleSignOut = () => {
    setUser(null)
    localStorage.removeItem('gardenPlannerUser')
  }

  // Check for saved session and onboarding on mount
  React.useEffect(() => {
    const saved = localStorage.getItem('gardenPlannerUser')
    const savedOnboarding = localStorage.getItem('gardenPlannerOnboarding')
    
    if (saved) {
      try {
        const parsedUser = JSON.parse(saved)
        if (TEST_MODE.AUTO_PREMIUM) {
          parsedUser.isPremium = true
        }
        setUser(parsedUser)
        
        // Show onboarding for new users
        if (!savedOnboarding && parsedUser.isNewUser) {
          setShowOnboarding(true)
        }
      } catch (e) {
        console.error('Failed to parse saved user')
      }
    }
    
    if (savedOnboarding) {
      try {
        setOnboardingData(JSON.parse(savedOnboarding))
      } catch (e) {
        console.error('Failed to parse saved onboarding')
      }
    }
    
    setIsLoading(false)
  }, [])

  const handleOnboardingComplete = (data: OnboardingData) => {
    setOnboardingData(data)
    localStorage.setItem('gardenPlannerOnboarding', JSON.stringify(data))
    setShowOnboarding(false)
    
    // Update user with zone info
    if (user && data.zone) {
      const updatedUser = { ...user, zipCode: data.zone }
      setUser(updatedUser)
      localStorage.setItem('gardenPlannerUser', JSON.stringify(updatedUser))
    }
  }

  const isActive = (path: string) => location.pathname === path

  return (
    <GardenProvider>
    <div className="app" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{ 
        background: 'linear-gradient(135deg, #2d5a27, #4a7c43)', 
        color: 'white',
        padding: '1rem 2rem',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          flexWrap: 'wrap', 
          gap: '1rem',
          maxWidth: '1400px',
          margin: '0 auto'
        }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', margin: 0 }}>🌱 Muddy Boots Garden Planner</h1>
            <nav style={{ display: 'flex', gap: '1.5rem', marginTop: '0.5rem' }}>
              <Link to="/" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/') ? 1 : 0.8 }}>Home</Link>
              {user && (
                <>
                  <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/dashboard') ? 1 : 0.8 }}>Dashboard</Link>
                  <Link to="/designer" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/designer') ? 1 : 0.8 }}>Designer</Link>
                  <Link to="/plants" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/plants') ? 1 : 0.8 }}>Plants</Link>
                  <Link to="/calendar" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/calendar') ? 1 : 0.8 }}>Calendar</Link>
                  <Link to="/alerts" style={{ color: 'white', textDecoration: 'none', opacity: isActive('/alerts') ? 1 : 0.8 }}>Alerts</Link>
                </>
              )}
            </nav>
          </div>
          
          <div>
            {user ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ opacity: 0.9 }}>{user.email}</span>
                <button 
                  onClick={handleSignOut}
                  style={{ 
                    background: 'rgba(255,255,255,0.2)', 
                    color: 'white', 
                    border: 'none', 
                    padding: '0.5rem 1rem', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Sign Out
                </button>
              </div>
            ) : (
              <button 
                onClick={() => setShowAuth(true)}
                style={{ 
                  background: 'white', 
                  color: '#2d5a27', 
                  padding: '0.75rem 1.5rem', 
                  borderRadius: '6px', 
                  border: 'none', 
                  cursor: 'pointer', 
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                🔐 Sign In / Sign Up
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ flex: 1, background: '#f5f5f0' }}>
        <Routes>
          <Route path="/" element={<Home user={user} onSignInClick={() => setShowAuth(true)} />} />
          <Route path="/dashboard" element={user ? <Dashboard user={user} /> : <Home user={user} onSignInClick={() => setShowAuth(true)} />} />
          <Route path="/designer" element={user ? <GardenDesigner /> : (isLoading ? <div style={{padding: '2rem', textAlign: 'center'}}>Loading...</div> : <Home user={user} onSignInClick={() => setShowAuth(true)} />)} />
          <Route path="/plants" element={<PlantDatabase />} />
          <Route path="/calendar" element={user ? <Calendar /> : <Home />} />
          <Route path="/alerts" element={user ? <WeatherAlertsPage /> : <Home />} />
          <Route path="/pricing" element={<PricingPage />} />
        </Routes>
      </main>

      {/* Onboarding Wizard */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onSkip={() => setShowOnboarding(false)}
        />
      )}

      {/* Auth Modal */}
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} onAuth={handleAuth} />
      
      {/* Premium Banner - Hidden in test mode or shown as premium active */}
      {user && !TEST_MODE.AUTO_PREMIUM && !hasPremiumAccess(user) && <PremiumBanner isPremium={user.isPremium} onUpgrade={() => {}} />}
      
      {/* Test Mode Indicator */}
      {TEST_MODE.SHOW_INDICATOR && (
        <div style={{
          position: 'fixed',
          bottom: '10px',
          right: '10px',
          background: '#ff6b35',
          color: 'white',
          padding: '0.5rem 1rem',
          borderRadius: '4px',
          fontSize: '0.8rem',
          fontWeight: 'bold',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}>
          🧪 TEST MODE - All Features Unlocked
        </div>
      )}
    </div>
    </GardenProvider>
  )
}

export default App
